import { Combobox as ComboboxPrimitive } from '@base-ui/react'
import { Check, ChevronDown, Search, SearchX } from 'lucide-react'

import {
  CONTROL_HEIGHT,
  CONTROL_ICON_SIZE,
  POPOVER_OPTION,
  controlShellClass,
  type ControlSize,
} from '@/components/ui/control'
import { cn } from '@/lib/cn'

/* The combobox — shadcn/ui's `combobox`, vendored and retinted.
 *
 * This is the ONE Base UI primitive in the codebase, and it is here on request:
 * the country picker was specified as "a shadcn/ui Country Dropdown", and that
 * component is built on this. Everything else in components/ui is hand-rolled
 * against the tokens in control.ts.
 *
 * It is VENDORED rather than installed by `shadcn add`, and that is the whole
 * design decision in this file. The CLI would have written a components.json, a
 * lib/utils, and a theme layer into styles/index.css declaring `--popover`,
 * `--accent`, `--muted-foreground` and the rest of shadcn's palette. This
 * project's palette shares NOT ONE name with it — surfaces are
 * `--color-surface-sunken`, text is `--color-text-muted`, the overlay shadow is
 * `--shadow-overlay` — so the installed component would have arrived styled
 * from a second, parallel design system sitting inside the first. Two token sets
 * in one stylesheet is how a product ends up with two greys.
 *
 * So the STRUCTURE and behaviour below are the registry's — Base UI's filtering,
 * its portal and positioner, its `data-highlighted` item states, its
 * `aria-activedescendant` handling — and every class is this system's:
 * `controlShellClass` for the box, `POPOVER_SURFACE` for the overlay,
 * `POPOVER_OPTION` for the rows. Identical to a Select on screen, which is the
 * point: a picker that opens the same overlay as every other picker.
 *
 * Only the parts the country dropdown needs are exported. Chips, groups and
 * separators from the registry item are omitted rather than kept unused — a
 * multi-select chip surface nothing renders is a surface nobody maintains.
 */

export const Combobox = ComboboxPrimitive.Root

/* ── The two shapes, and which one a caller gets ────────────────────────────
 *
 * Base UI supports both, and this file exports both:
 *
 *   INPUT-ANCHORED — `ComboboxInput`. The box on the form IS the `<input>` that
 *   carries `role="combobox"`; typing in place filters. Nothing renders inside
 *   the popup but the list.
 *
 *   TRIGGER-ANCHORED — `ComboboxTrigger` + `ComboboxSearch`. The box on the form
 *   is a BUTTON showing the selected value, and the filter field lives at the
 *   top of the popup, the way this project's Select and the shadcn country
 *   dropdown both work.
 *
 * An earlier version of this file said the second shape was impossible — that a
 * Trigger suppresses the Input entirely, leaving a search icon above an empty
 * space. It is not: a Trigger outside the popup and an Input inside it compose
 * correctly in @base-ui/react 1.7, which is what CountryDropdown and PhoneInput
 * now use, and CountryDropdown.test.tsx types into that field on every run. The
 * note is corrected rather than deleted because the failure it describes is real
 * if the Input is placed OUTSIDE the popup alongside the Trigger — two anchors,
 * one role.
 *
 * Pick by list length and by what the box has to say when closed. A picker whose
 * value is a country reads better as a button carrying a flag and a name; a
 * free-text search over a large list reads better as a field.
 */

/** The control on the form: the input, its icon, and the chevron.
 *
 *  `InputGroup` is Base UI's wrapper for "the input and its associated
 *  controls", which is what lets the whole box carry the control shell while the
 *  `<input>` inside it stays a bare transparent field. */
export function ComboboxInput({
  className,
  /* `inputSize`, not `size`. A native `<input>` HAS a `size` attribute — a
     number, the width in characters — so intersecting our own `'sm' | 'md'` onto
     these props collapsed the field to `never` and made every call site an
     error. Select names its own `selectSize` for the same collision. */
  inputSize = 'md',
  icon,
  ...props
}: ComboboxPrimitive.Input.Props & {
  inputSize?: ControlSize
  icon?: React.ReactNode
}) {
  return (
    <ComboboxPrimitive.InputGroup
      className={cn(
        controlShellClass(inputSize),
        'group/combobox flex items-center gap-2 px-3',
        className,
      )}
    >
      {icon}
      <ComboboxPrimitive.Input
        className={cn(
          'text-text-body min-w-0 flex-1 border-0 bg-transparent p-0',
          'font-sans text-[13px] font-normal outline-none',
          'placeholder:text-text-faint',
          'focus-visible:shadow-none',
          'disabled:cursor-not-allowed',
        )}
        {...props}
      />
      {/* Opens the list without typing, for the reader who wants to browse the
          249 rather than search them. `render` — Base UI's composition prop, in
          place of Radix's asChild — so the chevron IS the trigger button rather
          than sitting inside one. */}
      <ComboboxPrimitive.Trigger
        render={
          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            className="flex shrink-0 cursor-pointer items-center"
          />
        }
      >
        <ChevronDown
          size={14}
          aria-hidden
          className={cn(
            'text-text-muted transition-transform',
            'duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
            'data-popup-open:rotate-180',
          )}
        />
      </ComboboxPrimitive.Trigger>
    </ComboboxPrimitive.InputGroup>
  )
}

/** The trigger-anchored control: a button that shows what is selected.
 *
 *  Same shell, same height and same chevron as Select's trigger, because on a
 *  form the two must be indistinguishable — the difference between them is what
 *  the popup contains, not what the field looks like. */
export function ComboboxTrigger({
  className,
  children,
  triggerSize = 'md',
  /** Drops the control shell — the border, the fill and the height — for a
   *  trigger that sits INSIDE another control's box, as the calling-code button
   *  does inside PhoneInput. Two nested boxes read as a bug, not as a group. */
  bare = false,
  ...props
}: ComboboxPrimitive.Trigger.Props & {
  triggerSize?: ControlSize
  bare?: boolean
}) {
  return (
    <ComboboxPrimitive.Trigger
      // Explicit, because this trigger lives inside FormSheet's `<form>` and a
      // `<button>` with no type submits it — opening the country list would
      // have saved the sheet.
      type="button"
      className={cn(
        bare
          ? 'flex cursor-pointer items-center gap-1.5 self-stretch text-left outline-none'
          : cn(
              controlShellClass(triggerSize),
              'flex cursor-pointer items-center gap-2 px-3 text-left',
              'focus-visible:border-brand focus-visible:outline-none',
              CONTROL_HEIGHT[triggerSize],
            ),
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDown
        size={14}
        aria-hidden
        className={cn(
          'text-text-muted ml-auto shrink-0 transition-transform',
          'duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
          'data-popup-open:rotate-180',
        )}
      />
    </ComboboxPrimitive.Trigger>
  )
}

/** What the trigger says. Base UI renders no element of its own for this, so
 *  the caller supplies the markup — a flag beside a name, usually. */
export const ComboboxValue = ComboboxPrimitive.Value

/** The filter at the top of the popup, for the trigger-anchored shape.
 *
 *  Pinned above the list with a hairline under it rather than floating in the
 *  padding: over 249 rows the field has to stay put while the list scrolls, and
 *  the rule is what stops it reading as the first row. */
export function ComboboxSearch({
  className,
  ...props
}: ComboboxPrimitive.Input.Props) {
  return (
    <div
      className={cn(
        'border-border-subtle -mx-1 -mt-1 mb-1 flex items-center gap-2 border-b px-3',
        'h-9',
        className,
      )}
    >
      <Search
        size={CONTROL_ICON_SIZE}
        aria-hidden
        className="text-text-faint pointer-events-none shrink-0"
      />
      <ComboboxPrimitive.Input
        className={cn(
          'text-text-body min-w-0 flex-1 border-0 bg-transparent p-0',
          'font-sans text-[13px] font-normal outline-none',
          'placeholder:text-text-faint',
        )}
        {...props}
      />
    </div>
  )
}

/* The overlay. Portaled for the reason Select's listbox is: inside a FormSheet
   the popup was clipped by the scrollable body. `--anchor-width` is Base UI's
   own measurement of the trigger, which is how the popup matches its width
   without the manual anchoring hook the hand-rolled controls need. */
export function ComboboxContent({
  className,
  sideOffset = 4,
  ...props
}: ComboboxPrimitive.Popup.Props & {
  sideOffset?: ComboboxPrimitive.Positioner.Props['sideOffset']
}) {
  return (
    <ComboboxPrimitive.Portal>
      {/* z-60, not the z-40 every in-page popover uses, and not the registry's
          own value either. This popup portals to document.body, so inside a
          FormSheet it is a SIBLING of the sheet rather than a descendant: at
          z-40 it lands under the sheet's z-50 scrim and opaque panel, and the
          country picker reads as a dropdown that does not open. Select.tsx
          resolves the identical collision the identical way on its own
          positioner — see its comment on the same number — which is why the
          mode Select works in this sheet and this control did not. */}
      <ComboboxPrimitive.Positioner
        sideOffset={sideOffset}
        align="start"
        className="z-[60]"
      >
        <ComboboxPrimitive.Popup
          className={cn(
            // POPOVER_SURFACE's own `absolute` is dropped: the positioner
            // already owns placement here, and a second absolute inside it
            // fights the transform it sets.
            'bg-surface border-border-subtle rounded-md border p-1',
            'shadow-[var(--shadow-overlay)]',
            'w-(--anchor-width) max-w-(--available-width)',
            'origin-(--transform-origin)',
            'transition-[opacity,transform] duration-[var(--dur-fast)]',
            'ease-[var(--ease-standard)]',
            'data-starting-style:scale-[0.98] data-starting-style:opacity-0',
            'data-ending-style:scale-[0.98] data-ending-style:opacity-0',
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  )
}

export function ComboboxList({
  className,
  ...props
}: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      className={cn('asa-scroll max-h-[260px] overflow-y-auto', className)}
      {...props}
    />
  )
}

/** One row. `POPOVER_OPTION` plus the two states Base UI drives: the keyboard
 *  highlight and the tick on the selected row. */
export function ComboboxItem({
  className,
  children,
  ...props
}: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      className={cn(
        POPOVER_OPTION,
        'text-text-body font-normal',
        'data-highlighted:bg-surface-hover',
        'data-selected:text-text-heading data-selected:font-medium',
        'data-disabled:cursor-not-allowed data-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
      {/* Redundant with the selected state a screen reader already announces,
          hence aria-hidden: this tick is for the eye, as on Select. */}
      <ComboboxPrimitive.ItemIndicator
        render={<span className="ml-auto shrink-0" />}
      >
        <Check size={14} aria-hidden className="text-brand" />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  )
}

/** Nothing matched. Centred and given air, so an empty result reads as an
 *  ANSWER rather than as one greyed-out row the eye tries to click — the
 *  wording and shape are Select's, because the two must agree. */
export function ComboboxEmpty({
  className,
  children,
  hint,
  ...props
}: ComboboxPrimitive.Empty.Props & { hint?: React.ReactNode }) {
  return (
    <ComboboxPrimitive.Empty
      className={cn(
        'flex flex-col items-center gap-0.5 px-3 py-6 text-center',
        className,
      )}
      {...props}
    >
      <SearchX size={18} aria-hidden className="text-text-faint mb-0.5" />
      <span className="text-text-body text-[12.5px] font-normal">
        {children}
      </span>
      {hint ? (
        <span className="text-text-muted text-[11.5px] font-light">{hint}</span>
      ) : null}
    </ComboboxPrimitive.Empty>
  )
}

/** Exported so a caller can size a trigger to match the fields around it
 *  without importing control.ts as well. */
export { CONTROL_HEIGHT as COMBOBOX_HEIGHT }
